import { useState, useEffect } from 'react';
import { Search, UserPlus, Loader2, User, Phone, Mail, X } from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useCustomers, Customer } from '@/hooks/useCustomers';
import { toast } from 'sonner';

interface AddCustomerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectCustomer: (customer: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null) => void;
  selectedCustomer: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
  hideNewCustomerButton?: boolean;
}

type ViewMode = 'search' | 'create';

export function AddCustomerModal({
  open,
  onOpenChange,
  onSelectCustomer,
  selectedCustomer,
  hideNewCustomerButton = false,
}: AddCustomerModalProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { customers, isLoading, createCustomer } = useCustomers(debouncedSearch);

  // New customer form state
  const [newCustomer, setNewCustomer] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setViewMode('search');
      setSearchQuery('');
      setDebouncedSearch('');
      setNewCustomer({ first_name: '', last_name: '', email: '', phone: '' });
    }
  }, [open]);

  const handleSelectCustomer = (customer: Customer) => {
    onSelectCustomer({
      id: customer.id,
      first_name: customer.first_name,
      last_name: customer.last_name,
      email: customer.email,
      phone: customer.phone,
    });
    onOpenChange(false);
  };

  const handleRemoveCustomer = () => {
    onSelectCustomer(null);
    onOpenChange(false);
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.first_name.trim()) {
      toast.error('First name required', {
        description: 'Please enter a first name for the customer',
      });
      return;
    }

    try {
      const created = await createCustomer.mutateAsync({
        first_name: newCustomer.first_name.trim(),
        last_name: newCustomer.last_name.trim() || undefined,
        email: newCustomer.email.trim() || undefined,
        phone: newCustomer.phone.trim() || undefined,
      });

      onSelectCustomer({
        id: created.id,
        first_name: created.first_name,
        last_name: created.last_name,
        email: created.email,
        phone: created.phone,
      });

      toast.success('Customer created', {
        description: `${created.first_name} ${created.last_name || ''} has been added`,
      });

      onOpenChange(false);
    } catch (error) {
      toast.error('Error creating customer', {
        description: 'Please try again',
      });
    }
  };

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent maxWidth="md">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>
            {viewMode === 'search' ? 'Select Customer' : 'New Customer'}
          </ResponsiveModalTitle>
          <ResponsiveModalDescription>
            {viewMode === 'search'
              ? hideNewCustomerButton
                ? 'Search for an existing customer'
                : 'Search for an existing customer or create a new one'
              : 'Add a new customer to your organization'}
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="min-h-[300px]">
          {viewMode === 'search' ? (
            <>
              {/* Search Input */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  autoFocus
                />
              </div>

              {/* Currently Selected Customer */}
              {selectedCustomer && (
                <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/20 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {selectedCustomer.first_name} {selectedCustomer.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">Currently selected</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveCustomer}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                </div>
              )}

              {/* Customer List */}
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : customers.length === 0 ? (
                  <div className="text-center py-8">
                    <User className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {searchQuery ? 'No customers found' : 'No customers yet'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {searchQuery
                        ? 'Try a different search or create a new customer'
                        : 'Create your first customer'}
                    </p>
                  </div>
                ) : (
                  customers.map((customer) => (
                    <button
                      key={customer.id}
                      onClick={() => handleSelectCustomer(customer)}
                      className={cn(
                        'w-full p-3 rounded-lg border text-left transition-colors',
                        'hover:bg-muted/50 hover:border-primary/30',
                        selectedCustomer?.id === customer.id && 'border-primary bg-primary/5'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <User className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {customer.first_name} {customer.last_name}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {customer.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {customer.phone}
                              </span>
                            )}
                            {customer.email && (
                              <span className="flex items-center gap-1 truncate">
                                <Mail className="h-3 w-3" />
                                {customer.email}
                              </span>
                            )}
                          </div>
                        </div>
                        {customer.loyalty_points && customer.loyalty_points > 0 && (
                          <div className="text-xs text-primary font-medium">
                            {customer.loyalty_points} pts
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            /* Create New Customer Form */
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    placeholder="John"
                    value={newCustomer.first_name}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, first_name: e.target.value })
                    }
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    placeholder="Doe"
                    value={newCustomer.last_name}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, last_name: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={newCustomer.email}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, email: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={newCustomer.phone}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, phone: e.target.value })
                  }
                />
              </div>
            </div>
          )}
        </ResponsiveModalBody>

        <ResponsiveModalFooter>
          {viewMode === 'search' ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {!hideNewCustomerButton && (
                <Button
                  variant="blaze"
                  onClick={() => setViewMode('create')}
                  className="gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  New Customer
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setViewMode('search')}>
                Back
              </Button>
              <Button
                variant="blaze"
                onClick={handleCreateCustomer}
                disabled={createCustomer.isPending || !newCustomer.first_name.trim()}
                className="gap-2"
              >
                {createCustomer.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4" />
                )}
                Create Customer
              </Button>
            </>
          )}
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
